import Modal from "components/modal";
import CloseIcon from "assets/icons/close.svg";
import { Button } from "components/button";
import WeightInput from "components/weight-input";
import RadioGroup from "components/radio-group";
import DonationWeightChart from "components/donation-weight-chart";
import { useState, useEffect, useRef } from "react";
import { calculateEstimate } from "lib/calculateEstimate";
import InputValidation from "components/input-validation";
import ReplayLogo from "assets/replaylogo.png";
import { useUpdateTakeback } from "api/use-takebacks";
import WeightEstimation from "components/weight-estimation";
import { localizeDecimalSeparator } from "lib/utils";
import { useCurrentLocation } from "lib/utils";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Checklist from "components/checklist";
import { Link } from "react-router-dom";
import { EVENT_32 } from "utils/tracking";

export default function EditDonationModal({
  setModalOpen,
  modalOpen,
  currentWeight,
  replayWeight = 0,
  replayPercentage = 0,
  caseId,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState(
    replayPercentage && parseFloat(replayPercentage) > 0 ? "donation" : null
  );
  const [donationWeight, setDonationWeight] = useState(
    replayPercentage && parseFloat(replayPercentage) > 0
      ? Math.round(replayPercentage).toString()
      : ""
  );
  const [warningMessage, setWarningMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecklistChecked, setIsChecklistChecked] = useState(false);
  const { location: currentLocation } = useCurrentLocation();
  const modalContainerRef = useRef(null);
  const [replayModalOpen, setReplayModalOpen] = useState(false);

  const { mutate: updateTakeback } = useUpdateTakeback({
    onSuccess: () => {
      setModalOpen(false);
      onSuccess();
    },
    onError: (error) => {
      console.error("Error updating takeback:", error);
      setIsSubmitting(false);
    },
  });

  // Handle scroll to top and overflow control when spinner is showing
  useEffect(() => {
    if (isSubmitting && modalContainerRef.current) {
      // Scroll to top instantly
      modalContainerRef.current.scrollTo({
        top: 0,
        behavior: "auto",
      });

      // Set overflow hidden after a few ms
      const timeoutId = setTimeout(() => {
        if (modalContainerRef.current) {
          modalContainerRef.current.style.overflow = "hidden";
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    } else if (modalContainerRef.current) {
      // Reset overflow when not submitting
      modalContainerRef.current.style.overflow = "";
    }
  }, [isSubmitting]);

  const donationPercentage = parseInt(donationWeight) || 0;
  const actualDonationWeight = currentWeight * (donationPercentage / 100);
  const remainingWeight = currentWeight - actualDonationWeight;

  const handleDonationWeightChange = (event) => {
    let newValue = event.target.value.replace(/[^0-9]/g, ""); // Only allow digits
    newValue = newValue.slice(0, 3); // Limit to 3 digits
    setDonationWeight(newValue);

    const newPercentage = parseInt(newValue);
    if (!isNaN(newPercentage) && newPercentage > 100) {
      setWarningMessage(
        `You cannot donate more than 100% of your total weight.`
      );
    } else {
      setWarningMessage("");
    }
  };

  //reset donation weight when no donation is selected
  useEffect(() => {
    if (selectedOption === "no_donation") {
      setDonationWeight("");
    }
  }, [selectedOption]);

  const handleConfirm = () => {
    setIsSubmitting(true);
    updateTakeback({
      caseId,
      originalReplayWeight:
        selectedOption === "donation" ? parseFloat(actualDonationWeight) : 0,
      replayPercentage:
        selectedOption === "donation" ? parseFloat(donationWeight) : 0,
    });
  };

  const radioOptions = [
    {
      value: "donation",
      title: "Yes, donate a portion of my LEGO bricks.",
      subtitle: "Any donated bricks will not earn a reward.",
    },
    {
      value: "no_donation",
      title: "No, receive the full trade-in value of my LEGO bricks.",
      subtitle: "Once verified, you'll be sent a LEGO e-Gift Card.",
    },
  ];

  const estimate = calculateEstimate(remainingWeight.toString());
  const originalEstimate = calculateEstimate(currentWeight.toString());

  return (
    <>
      <Modal
        setModalOpen={setModalOpen}
        modalOpen={modalOpen}
        className="flex flex-col justify-between max-h-[90vh]"
        isLoading={isSubmitting}
        loadingMessage="Updating donation..."
        ref={modalContainerRef}
      >
        <div className="flex flex-col space-y-x-large py-4">
          <div className="flex justify-between mb-4">
            <h2 className="text-4xl font-semibold leading-none">
              {!replayPercentage ? "Add donation" : "Edit donation"}
            </h2>
            <div className="cursor-pointer" onClick={() => setModalOpen(false)}>
              <img src={CloseIcon} alt="" variant="secondary" />
            </div>
          </div>

          <div className="flex flex-col space-y-regular">
            <img src={ReplayLogo} alt="Replay Logo" className="w-[80px] mb-2" />
            <h2 className="text-primary font-bold text-lg">
              Optional Donation to LEGO® Replay
            </h2>
            <p className="text-tertiary">
              Would you like to donate a portion of your bricks to LEGO Replay?
              LEGO Replay gives pre-loved bricks to kids in need of play across
              the US.{" "}
              <span
                className="text-blue cursor-pointer"
                tabIndex={0}
                role="button"
                onClick={() => setReplayModalOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setReplayModalOpen(true);
                }}
              >
                Learn more.
              </span>
            </p>
          </div>

          <div>
            <h3 className="">
              Would you like to donate bricks to LEGO Replay?
            </h3>
            <RadioGroup
              options={radioOptions}
              name="custom-radio"
              selectedOption={selectedOption}
              onChange={setSelectedOption}
              className="mt-2"
            />
          </div>

          {selectedOption === "donation" && (
            <div className="flex flex-col space-y-regular">
              <p>
                Enter the percentage of verified LEGO bricks and elements you'd
                like to donate*
              </p>
              <WeightInput
                hideCaption
                placeholder="0"
                unit="%"
                className="max-w-6xl sm:max-w-64 mb-8"
                value={donationWeight}
                onChange={handleDonationWeightChange}
                locale="en-US"
              />
              {parseFloat(donationWeight) > 0 &&
                donationWeight <= 100 &&
                selectedOption === "donation" && (
                  <div className="pt-4 w-full">
                    <div className="flex items-center justify-between bg-gray-300/25 px-6 h-[50px] rounded-lg">
                      <div className="font-bold">
                        {actualDonationWeight.toFixed(2)} lbs
                      </div>
                      <div className="w-[1px] h-[35px] bg-gray-300"></div>
                      <div className="font-bold text-[#4C2F92] text-sm">
                        Thank you for choosing to donate to LEGO Replay.
                      </div>
                    </div>
                  </div>
                )}
              <p className="text-black-300 text-sm">
                *Any donated bricks will not earn a reward.
              </p>
              {warningMessage && (
                <InputValidation errorMessage={warningMessage} />
              )}
              {parseFloat(donationWeight) > 0 &&
                parseFloat(donationWeight) <= 100 && (
                  <div className="pt-xx-large flex flex-col space-y-regular">
                    <h3 className="text-2xl font-bold">
                      Your estimated trade-in breakdown
                    </h3>
                    <p className="">
                      The final reward and donation values will be based on the
                      verified weight of eligible LEGO bricks and elements.
                    </p>
                    <DonationWeightChart
                      totalWeight={currentWeight}
                      replayWeight={actualDonationWeight}
                      showTotalEstimatedWeight={true}
                      showBreakdown={true}
                      showBackground={true}
                      replayPercentage={donationWeight}
                    />
                  </div>
                )}
            </div>
          )}

          {!warningMessage && !donationWeight && (
            <WeightEstimation
              title="YOUR REWARD WILL BE WORTH"
              visual="giftcard"
              singleValue={localizeDecimalSeparator(
                estimate.gift,
                currentLocation
              )}
              description="Paid out as a LEGO® e-Gift Card."
              additionalDescription={`Original estimate: $${localizeDecimalSeparator(originalEstimate.gift, currentLocation)} (${currentWeight} lbs)  •  Donation: ${actualDonationWeight} lbs`}
              animate
              locale={currentLocation?.locale}
            />
          )}

          {!replayPercentage && selectedOption === "donation" && (
            <div className="">
              <Checklist
                checklistItems={[
                  {
                    name: "replay_terms_agreement",
                    label: (
                      <span>
                        I understand I am donating a portion of my bricks to
                        LEGO Replay and will receive no remuneration for my
                        selection. See{" "}
                        <Link
                          to={t("urls.replay_terms_and_conditions")}
                          className="text-blue"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          LEGO Replay Terms & Conditions
                        </Link>
                        .
                      </span>
                    ),
                  },
                ]}
                setAllChecked={setIsChecklistChecked}
              />
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <Button
            className=""
            variant="secondary"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className=""
            onClick={handleConfirm}
            disabled={
              selectedOption === "donation" &&
              (!donationWeight ||
                parseFloat(donationWeight) === 0 ||
                parseFloat(donationWeight) > 100 ||
                parseFloat(actualDonationWeight) === parseFloat(replayWeight) ||
                (!replayPercentage && !isChecklistChecked))
            }
            tracking={[EVENT_32]}
          >
            Confirm
          </Button>
        </div>
      </Modal>
      <AnimatePresence>
        {replayModalOpen && (
          <Modal setModalOpen={setReplayModalOpen} modalOpen={replayModalOpen}>
            <div className="flex flex-col gap-large py-4">
              <div className="flex justify-between mb-4">
                <h2 className="text-4xl font-semibold leading-none text-left">
                  What is LEGO Replay?
                </h2>
                <div
                  className="cursor-pointer"
                  onClick={() => setReplayModalOpen(false)}
                >
                  <img src={CloseIcon} alt="Close" />
                </div>
              </div>
              <div>
                <p className="text-base text-tertiary text-left">
                  LEGO Replay is a sustainable way to keep as many bricks in
                  play as possible and share the joy of LEGO play with kids who
                  might not have access. Donated bricks are sorted, cleaned, and
                  packed into Replay boxes that are distributed via The LEGO
                  Group's trusted community partners to impact more children.
                </p>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
}
